import React, {useEffect, useMemo, useRef, useState} from "react";
import DemographicSelectionForm from "./DemographicSelectionForm";
import {demographicProfileToForm, demographicProfilePayload, supportsDemographicSelection, validateDemographicForm} from "./DemographicSelectionProfile";
import {Dialog, DialogActionsBar} from "@progress/kendo-react-dialogs";
import {Button} from "@progress/kendo-react-buttons";
import {Checkbox, RadioButton} from "@progress/kendo-react-inputs";
import {Tooltip} from "@progress/kendo-react-tooltip";
import {semcastAxios, showErrorToastMessage, showSuccessToastMessage} from "../../../Utils/Common";
import {
    DETECT_MAP,
    GROUPS_ACCESSIBLE_PIXELS,
    IDENTITY_PIPELINE
} from "../../../api/constants";
import LoadOverlay from "../../Components/SharedComponents/LoadOverlay";
import {DropDownList, MultiSelect} from "@progress/kendo-react-dropdowns";
import FileTypeSelector from "./FileTypeSelector";
import * as Yup from "yup";
import FileBrowser from "../../Components/Onboarding/FileBrowser";
import PermissionsEditGroup from "../../Components/Permission/PermissionsEditGroup";
import EditAppendTree from "./EditAppendTree";
import {getFTPSettings} from "../../../actions/OnBoardingActions";
import {
    COMPRESSION_DEFAULT_ITEM,
    COMPRESSION_OPTIONS,
    inferCompressionFromPath,
    CONNECTION_TYPE_OPTIONS,
    CREATE_ITEM,
    dayOfWeekOptions,
    DEFAULT_GROUP_ITEM,
    EXPORT_TYPE_OPTIONS,
    FTPEmbeddedForm,
    getById,
    INITIAL_FIELD_ERRORS,
    normalizeConnections,
    renderDetectMapInsights,
    S3EmbeddedForm,
    SCHEDULE_TYPE_ALL,
    scheduleConfirmText,
    scheduleLabel,
    setDomRef
} from "./IdentityPipelineSharedUtil";
import {UsersHelpers} from "../../../helpers/UsersHelpers";
import {loadUsers} from "../../../actions/UserActions";
import {useFormik} from "formik";
import {UserContext} from "../../../contexts/UserContext";

/* ------------------------------------------------------------------ */
/* Main EditIdentityPipeline component                               */
/* ------------------------------------------------------------------ */
export default function EditIdentityPipeline({open, onClose, onCreated, pipelineId}) {
    const {user} = React.useContext(UserContext);
    const isSuperAdmin = !!user?.isAdmin;
    const [pipelineType, setPipelineType] = useState(EXPORT_TYPE_OPTIONS[0]); // default File Exchange
    const isFileExchange = pipelineType?.id === "FILE_EXCHANGE" || pipelineType?.id === "FOLDER_EXCHANGE";
    const scheduleTypeOptions = useMemo(
        () => (isFileExchange ? SCHEDULE_TYPE_ALL : SCHEDULE_TYPE_ALL.filter(o => o.id !== "DAILY")),
        [isFileExchange]
    );

    // refs for focusing errors
    const nameRef = useRef(null);
    const inputConnRef = useRef(null);
    const outputConnRef = useRef(null);
    const chooseFileBtnRef = useRef(null);
    const weeklyDayRef = useRef(null);
    const groupRef = useRef(null);
    const scheduleTypeRef = useRef(null);
    const compressionRef = useRef(null);
    const fileTypeRef = useRef(null);
    const clientidColumnRef = useRef(null);
    const [finalAvailableAttributes, setFinalAvailableAttributes] = useState(null);
    const [finalSelectedAttributes, setFinalSelectedAttributes] = useState(null);
    const [overlayActive, setOverlayActive] = useState(false);
    // dialog-local state (mirrors original)
    const [pageLoader, setPageLoader] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({
        name: false,
        inputConn: false,
        outputConn: false,
        inputFile: false,
        weeklyDay: false,
        group: false,
        scheduleType: false,
        compressionType: false,
        fileType: false,
        clientIdColumn: false,
    });

    const [recordForm, setRecordForm] = useState({
        name: "",
        groupId: null,
        description: "",
        inputConnId: null,
        outputConnId: null,
        scheduleType: null,
        scheduleWeeklyDay: null,
        scheduleMonthlyDate: null,
        inputFileKey: null,
    });
    const [submitOverlayActive, setSubmitOverlayActive] = useState(false);
    const [groupsArr, setGroupsArr] = useState([]);
    const [groupsWithNewOption, setGroupsWithNewOption] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(DEFAULT_GROUP_ITEM);
    const [noTypeDialogOpen, setNoTypeDialogOpen] = useState(false);
    const [noTypeInfo, setNoTypeInfo] = useState(null);
    const [detectFileTypeOverlayActive, setDetectFileTypeOverlayActive] = useState(false);
    const [connections, setConnections] = useState([]);
    const [loadingConns, setLoadingConns] = useState(false);
    const [connError, setConnError] = useState(null);
    const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
    const [showInputBrowser, setShowInputBrowser] = useState(false);
    const [isOpenInputBrowserModal, setIsOpenInputBrowserModal] = useState(false);
    const [inputFilePath, setInputFilePath] = useState("");
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [availableAttributes, setAvailableAttributes] = useState(null);
    const [detectMapInfo, setDetectMapInfo] = useState(null);
    const [detectedFileTypes, setDetectedFileTypes] = useState([]);
    const [selectedFileType, setSelectedFileType] = useState(null);
    const [demographicForm, setDemographicForm] = useState(() => demographicProfileToForm(null));
    const [demographicErrorsVisible, setDemographicErrorsVisible] = useState(false);
    const [compressionType, setCompressionType] = useState(null);
    // New: detected columns from detectMap.columns and selected client id column
    const [detectedColumns, setDetectedColumns] = useState([]);
    const [selectedClientIdColumn, setSelectedClientIdColumn] = useState(null);
    const [selectedAdditionalClientColumns, setSelectedAdditionalClientColumns] = useState([]);
    const [triedSubmit, setTriedSubmit] = useState(false);
    const [allUsersRoles, setAllUsersRoles] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmDetails, setConfirmDetails] = useState([]);
    const [openEditCreateDialog, setOpenEditCreateDialog] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [userHelpers, setUserHelpers] = useState({});
    const [createGroupOverlayActive, setCreateGroupOverlayActive] = useState(false);
    // create-connection dialog
    const [showConnDialog, setShowConnDialog] = useState(false);
    const [connDialogOrigin, setConnDialogOrigin] = useState(null); // "input" | "output"
    const [connForm, setConnForm] = useState({type: "S3"});
    const [createdGroupId, setCreatedGroupId] = useState(null);
    const [selectedAttributesCount, setSelectedAttributesCount] = useState(0);
    const [folderExchangeInfoOpen, setFolderExchangeInfoOpen] = useState(false);
    const [transformationProcessingCheckbox, setTransformationProcessingCheckbox] = useState(false);
    const [transformationProcessingMode, setTransformationProcessingMode] = useState("APPEND");
    const [prependOneToPhone, setPrependOneToPhone] = useState(false);
    // helpers mapped 1:1
    const byId = (id) => connections.find((c) => c.id === id) || null;
    const ddValueConn = (id) => (id ? connections.find((c) => c.id === id) || null : null);
    const connectionOptions = useMemo(() => [...connections, CREATE_ITEM], [connections]);
    const phoneSelected = /"(?:hp|bp)\.phone"/.test(JSON.stringify(finalSelectedAttributes || selectedAttributes));
    const supportsPhoneFormatting = ["HOUSEHOLD_EXPORT", "PERSON_EXPORT", "BUSINESS_EXPORT"].includes(pipelineType?.id);
    const detectedColumnOptions = useMemo(
        () => detectedColumns.map((s) => ({text: s, value: s})),
        [detectedColumns]
    );
    const closeConfirmDialog = () => {
        setConfirmOpen(false);
        setConfirmDetails([]);
    };


    // --- Load one pipeline for edit ---
    async function loadPipelineById(id) {
        const res = await semcastAxios.get(IDENTITY_PIPELINE + "/" + id, {withCredentials: true});
        return res?.data?.data || {};
    }

    // load groups + connections when opened
    useEffect(() => {
        if (!open) return;
        setRecordForm({
            name: "",
            groupId: null,
            description: "",
            inputConnId: null,
            outputConnId: null,
            scheduleType: null,
            scheduleWeeklyDay: null,
            scheduleMonthlyDate: null,
            inputFileKey: null,
        });
        setSelectedGroup(DEFAULT_GROUP_ITEM);
        setDetectMapInfo(null);
        setDetectedFileTypes([]);
        setSelectedFileType(null);
        setDemographicForm(demographicProfileToForm(null));
        setDemographicErrorsVisible(false);
        setCompressionType(null);
        setInputFilePath("");

        (async () => {
            await Promise.allSettled([loadGroups(), reloadConnections()]);
        })();
    }, [open]);
    useEffect(() => {
        if (pipelineType?.id === "FOLDER_EXCHANGE") {
            setFolderExchangeInfoOpen(true);
            setRecordForm(prev => ({...prev, scheduleType: "UNSCHEDULED"}));
        } else {
            setFolderExchangeInfoOpen(false);
        }
    }, [pipelineType?.id]);

    /*      clear warning message for input fields   */
    useEffect(() => {
        if ((recordForm.name || "").trim()) {
            setFieldErrors((prev) => ({...prev, name: false}));
        }
    }, [recordForm.name]);

    useEffect(() => {
        if (recordForm.inputConnId) {
            setFieldErrors((prev) => ({...prev, inputConn: false}));
        }
    }, [recordForm.inputConnId]);

    useEffect(() => {
        if (recordForm.outputConnId) {
            setFieldErrors((prev) => ({...prev, outputConn: false}));
        }
    }, [recordForm.outputConnId]);
    useEffect(() => {
        if (pipelineType?.id !== "FILE_EXCHANGE" && selectedAdditionalClientColumns.length) {
            setSelectedAdditionalClientColumns([]);
        }
    }, [pipelineType?.id]);
    useEffect(() => {
        if (!selectedAdditionalClientColumns?.length) return;
        setSelectedAdditionalClientColumns((prev) => {
            const filtered = prev.filter((col) => detectedColumns.includes(col));
            if (filtered.length === prev.length && filtered.every((val, idx) => val === prev[idx])) {
                return prev;
            }
            return filtered;
        });
    }, [detectedColumns]);
    useEffect(() => {
        if (recordForm.groupId) setFieldErrors((p) => ({...p, group: false}));
    }, [recordForm.groupId]);

    useEffect(() => {
        if (recordForm.scheduleType) setFieldErrors((p) => ({...p, scheduleType: false}));
    }, [recordForm.scheduleType]);

    useEffect(() => {
        if (compressionType) setFieldErrors((p) => ({...p, compressionType: false}));
    }, [compressionType]);

    useEffect(() => {
        if (recordForm.scheduleType === "MONTHLY" && recordForm.scheduleMonthlyDate !== 1) {
            setRecordForm((prev) => {
                if (prev.scheduleMonthlyDate === 1) return prev;
                return {...prev, scheduleMonthlyDate: 1};
            });
        }
    }, [recordForm.scheduleType, recordForm.scheduleMonthlyDate]);

    useEffect(() => {
        if (selectedFileType) setFieldErrors((p) => ({...p, fileType: false}));
    }, [selectedFileType]);
    useEffect(() => {
        if (recordForm.scheduleWeeklyDay) setFieldErrors((p) => ({...p, weeklyDay: false}));
    }, [recordForm.scheduleWeeklyDay]);
    useEffect(() => {
        const newList = groupsArr.length > 0 ? [{
            group_name: "Create New Group",
            id: "new"
        }, ...groupsArr] : [...groupsArr];
        setGroupsWithNewOption(newList);
    }, [groupsArr]);

    useEffect(() => {
        setUserHelpers(new UsersHelpers());
    }, []);

    useEffect(() => {
        if (!open) return;
        setFieldErrors({...INITIAL_FIELD_ERRORS, clientIdColumn: false});
        setTriedSubmit(false);
    }, [open]);
    useEffect(() => {

        // Ensure connections/groups loaded before mapping IDs to dropdown values
        if (!connections.length || !groupsArr.length) return;
        (async () => {
            try {
                setSubmitOverlayActive(true);
                const r = await loadPipelineById(pipelineId);
                // parse schedule_properties safely
                let sched = {};
                try {
                    sched = typeof r.schedule_properties === "string"
                        ? JSON.parse(r.schedule_properties || "{}")
                        : (r.schedule_properties || {});
                } catch {
                    sched = {};
                }
                const s = sched.schedule || {};
                setPrependOneToPhone(!!sched.prepend_one_to_phone);
                // parse match_return_properties safely
                let attrs = {};
                try {
                    attrs = typeof r.match_return_properties === "string"
                        ? JSON.parse(r.match_return_properties || "{}")
                        : (r.match_return_properties || {});
                } catch {
                    attrs = {};
                }
                // LEFT: available_return_properties (NEW)
                let avail = {};
                try {
                    avail = typeof r.available_return_properties === "string"
                        ? JSON.parse(r.available_return_properties || "{}")
                        : (r.available_return_properties || {});
                } catch {
                    avail = {};
                }
                // incoming layout (detect map) if present
                let dm = null;
                try {
                    dm = typeof r.incoming_layout === "string"
                        ? JSON.parse(r.incoming_layout || "null")
                        : (r.incoming_layout || null);
                } catch {
                    dm = null;
                }
                let status = r.status || null;
                const pipelineTypeId = (r.pipeline_type || "FILE_EXCHANGE").toUpperCase();
                const pipelineTypeOption =
                    EXPORT_TYPE_OPTIONS.find((opt) => opt.id === pipelineTypeId) || EXPORT_TYPE_OPTIONS[0];
                setPipelineType(pipelineTypeOption);
                setDemographicForm(demographicProfileToForm(r.demographic_selection_profile, r.pipeline_type, r.file_type));

                const scheduleTypeValue = (s.type || "DAILY").toUpperCase();
                const mappedWeeklyDay = typeof s.dayOfWeek === "string" ? dayNameToId(s.dayOfWeek) : s.dayOfWeek ?? null;
                const normalizedDescription =
                    typeof r.description === "string" && r.description.toLowerCase() !== "null"
                        ? r.description
                        : "";
                setRecordForm(prev => ({
                    ...prev,
                    name: r.pipeline_name || "",
                    description: normalizedDescription,
                    groupId: r.group_id ?? null,
                    inputConnId: r.incoming_ftp_settings_id ? String(r.incoming_ftp_settings_id) : null,
                    outputConnId: r.exhaust_ftp_settings_id ? String(r.exhaust_ftp_settings_id) : null,
                    inputFileKey: r.incoming_file_key || null,
                    scheduleType: scheduleTypeValue,
                    scheduleWeeklyDay: scheduleTypeValue === "WEEKLY" ? mappedWeeklyDay : null,
                    scheduleMonthlyDate: scheduleTypeValue === "MONTHLY" ? (s.dayOfMonth ?? 1) : null,
                    status: status,
                }));
                setTransformationProcessingCheckbox(!!r.external_transformation);
                setTransformationProcessingMode(r.on_delivery_instructions === "REPLACE" ? "REPLACE" : "APPEND");
                // sync Save to Group dropdown object
                const grp = groupsArr.find(g => String(g.id) === String(r.group_id));
                if (grp) setSelectedGroup(grp);
                // set file/compression + attrs + detect map
                const fileTypeFromRecord = r.file_type || null;
                setSelectedFileType(fileTypeFromRecord);
                setCompressionType(r.compression_type);
                setSelectedAttributes(attrs);       // RIGHT
                setAvailableAttributes(avail);           // LEFT
                setDetectMapInfo(dm);
                const mapFileTypes = Array.isArray(dm?.fileTypes) ? dm.fileTypes : [];
                const mergedFileTypes = fileTypeFromRecord && !mapFileTypes.some(ft => ft?.type === fileTypeFromRecord)
                    ? [...mapFileTypes, {type: fileTypeFromRecord, confidence: null}]
                    : mapFileTypes;
                setDetectedFileTypes(mergedFileTypes);
                // Populate detected columns from existing incoming_layout
                const cols = Array.isArray(dm?.columns) ? dm.columns : [];
                const sources = cols.map(c => c?.source).filter(Boolean);
                const clientIdColumnRaw = r.client_id_column;
                const clientIdColumn = clientIdColumnRaw && clientIdColumnRaw.toLowerCase() !== "null" ? clientIdColumnRaw : null;
                const returnColumnsRaw = r.incoming_return_columns;
                const returnColumns =
                    typeof returnColumnsRaw === "string" && returnColumnsRaw.toLowerCase() !== "null"
                        ? returnColumnsRaw.split(",")
                        : [];
                // const additionalClientColumns = Array.isArray(r.incoming_return_columns)
                //     ? r.incoming_return_columns.filter(Boolean)
                //     : [];
                const additionalClientColumns = Array.isArray(returnColumns)
                    ? returnColumns.filter(Boolean)
                    : [];
                let mergedSources = clientIdColumn && !sources.includes(clientIdColumn)
                    ? [...sources, clientIdColumn]
                    : sources;
                if (additionalClientColumns.length) {
                    additionalClientColumns.forEach((col) => {
                        if (!mergedSources.includes(col)) mergedSources.push(col);
                    });
                }
                setDetectedColumns(mergedSources);
                setSelectedClientIdColumn(clientIdColumn);
                setSelectedAdditionalClientColumns(additionalClientColumns);
            } finally {
                setSubmitOverlayActive(false);
            }
        })();
    }, [open, pipelineId, connections, groupsArr]);
    useEffect(() => {

    }, [availableAttributes, selectedAttributes]);
    useEffect(() => {
        if (recordForm.inputFileKey) {
            setFieldErrors((prev) => ({...prev, inputFile: false}));
            setTriedSubmit(false);
        }
    }, [recordForm.inputFileKey]);

    const onScheduleTypeChange = (e) => {
        const item = e.value;
        const newType = item?.id || "DAILY";
        setRecordForm((prev) => ({
            ...prev,
            scheduleType: newType,
            scheduleWeeklyDay: newType === "WEEKLY" ? prev.scheduleWeeklyDay : null,
            scheduleMonthlyDate: newType === "MONTHLY" ? 1 : null,
        }));
        setFieldErrors((prev) => ({...prev, scheduleType: false, weeklyDay: false}));
        setTriedSubmit(false);
    };
    const onWeeklyDayChange = (e) => {
        const item = e.value;
        setRecordForm((prev) => ({...prev, scheduleWeeklyDay: item?.id ?? null}));
    };
    const handleTransformationProcessingChange = () => {
        setTransformationProcessingCheckbox((prev) => !prev);
    };
    const handleTransformationModeChange = (event) => {
        setTransformationProcessingMode(event.value);
    };
    const groupEditFormik = useFormik({
        initialValues: {
            group_name: '',
            group_id: '',
            userNRoles: []
        },
// We used Yup here.
        validationSchema: Yup.object().shape({
            group_name: Yup.string().required('Group Name is required'),
            userNRoles: Yup.array().of(
                Yup.object().shape({
                    user_id: Yup.string(),
                    user_name: Yup.string(),
                    role_id: Yup.number(),
                    group_id: Yup.number(),
                    founding_assignment: Yup.boolean()
                })
            )
        }),
        onSubmit: values => {
        },
    });

    // API loaders (same behavior)
    async function loadGroups() {
        try {
            const res = await semcastAxios.get(GROUPS_ACCESSIBLE_PIXELS, {withCredentials: true});
            setGroupsArr(res?.data?.data || []);
        } catch (e) {
            showErrorToastMessage("Error accessing Group information.");
            setGroupsArr([]);
        }
    }


    async function reloadConnections() {
        setLoadingConns(true);
        setConnError(null);
        try {
            const resp = await getFTPSettings();
            const list = Array.isArray(resp?.data) ? resp.data : [];
            const norm = normalizeConnections(list);
            setConnections(norm);
            return norm;
        } catch (e) {
            setConnError(e?.message || "Failed to load connections");
            setConnections([]);
            return [];
        } finally {
            setLoadingConns(false);
        }
    }

    const onRecordTextChange = (e) => {
        const {name, value} = e.target;
        setRecordForm((prev) => ({...prev, [name]: value}));
        if (name === "name" && value.trim()) {
            setFieldErrors((prev) => ({...prev, name: false}));
            setTriedSubmit(false);
        }
    };
    const resetGroupUserRolesForm = async (usersList) => {

        console.log("StartingrResetGroupUserRolesForm usersList=", usersList);
        let usersArr = usersList
        if (usersArr) {
            let userNRoles = usersArr.map(user => {
                return {
                    user_id: user.id,
                    user_name: `${user.first_name} ${user.last_name}`,
                    role_id: '',
                    group_id: '',
                    founding_assignment: false,
                    role_name: "Not a Member",
                }
            });

            console.log("ResetGroupUserRolesForm userNRoles=", userNRoles);
            setAllUsersRoles(userNRoles);

            await groupEditFormik.setValues({group_name: '', group_id: '', userNRoles})
            console.log("ResetGroupUserRolesForm userNRoles=", userNRoles);
        }
        // console.error("ResetGroupUserRolesForm userNRoles=", userNRoles);
    }
    const usersLoad = async () => {
        try {
            console.log("StartingUserSLoad");
            const values = await Promise.allSettled([loadUsers()]);
            console.log("UsersLoadVALUE:", values[0].value);

            // setUsers(values[0].value);

            await resetGroupUserRolesForm(values[0].value);
            return values;

        } catch (e) {
        }
    }
    const onChangeGroupDropDown = async (e) => {
        const item = e.value;
        if (!item) return;
        if (item.id === "new") {
            // Open modal to create a new group
            setSubmitOverlayActive(true);

            try {
                await usersLoad();
                setOpenEditCreateDialog(true);
            } catch (error) {
                console.error("Failed to load users and reset roles:", error);
                // Optionally handle the error, e.g., show an error message to the user
            } finally {
                setSubmitOverlayActive(false);
            }
            // snap back to placeholder; keep it invalid until a real group is chosen
            setSelectedGroup(DEFAULT_GROUP_ITEM);
            setRecordForm((prev) => ({...prev, groupId: null}));

            return;

            // You will need to pass a prop or use context to open the dialog from the second file
        }
        setSelectedGroup(item);
        const idNum =
            item.id === "-1" || item.id === -1 ? null : (typeof item.id === "string" ? Number(item.id) : item.id);
        setRecordForm((prev) => ({...prev, groupId: idNum}));
        setFieldErrors((prev) => ({...prev, group: false}));
        setTriedSubmit(false);
    };

    const onSelectConn = (which) => (e) => {
        const choice = e.value;
        if (choice?.id === "__create__") {
            setConnDialogOrigin(which);
            setShowConnDialog(true);
            setConnForm({type: "S3"});
            return;
        }
        setRecordForm((prev) => ({
            ...prev,
            [`${which}ConnId`]: choice?.id ?? null
        }));
        // clear error for the field the user just fixed
        setFieldErrors((prev) => ({
            ...prev, ...(which === "input" ? {inputConn: false} : {outputConn: false}),
        }));
        setTriedSubmit(false);
        if (which === "input") {
            setFieldErrors((prev) => ({...prev, inputFile: false}));
        }
    };

    // file browser
    const openInputBrowser = () => {
        if (!recordForm.inputConnId) return;
        setIsOpenInputBrowserModal(true);
        setShowInputBrowser(true);
    };

    const handleInputBrowserClose = async (didSelect, params = {}) => {
        setDetectFileTypeOverlayActive(true);
        setIsOpenInputBrowserModal(false);
        setShowInputBrowser(false);
        if (!didSelect) return;

        const path = params?.path;
        if (!path || !recordForm.inputConnId) return;

        setRecordForm((prev) => ({...prev, inputFileKey: path}));
        setInputFilePath(path);
        const inferredCompression = inferCompressionFromPath(path);
        if (inferredCompression) {
            setCompressionType(inferredCompression);
            setFieldErrors((prev) => ({...prev, compressionType: false}));
        }
        setFieldErrors((prev) => ({...prev, inputFile: false}));   // clear the error message
        setTriedSubmit(false);

        const ftpSettingsId = byId(recordForm.inputConnId)?._raw?.id || 0;

        try {
            const {data} = await semcastAxios.post(
                DETECT_MAP,
                {path, ftp_settings_id: ftpSettingsId},
                {headers: {"Content-Type": "application/json"}, withCredentials: true}
            );

            const dm = data?.data?.detectMap || null;
            setDetectMapInfo(dm);

            const ft = dm?.fileTypes ?? [];
            setDetectedFileTypes(Array.isArray(ft) ? ft : []);

            // Populate detectedColumns from detectMap.columns
            const cols = Array.isArray(dm?.columns) ? dm.columns : [];
            const sources = cols.map(c => c?.source).filter(Boolean);
            console.log(sources)
            setDetectedColumns(sources);
            setSelectedClientIdColumn(null);
            if (!sources.length) {
                setFieldErrors(prev => ({...prev, clientIdColumn: false}));
            }

            if (Array.isArray(ft) && ft.length === 0) {
                setNoTypeInfo(dm);
                setNoTypeDialogOpen(true);
            }

            if (Array.isArray(ft) && ft.length) {
                const best = [...ft].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0];
                setSelectedFileType(best?.type ?? null);
            } else {
                setSelectedFileType(null);
            }

        } catch {
            showErrorToastMessage("Failed to analyze file.");
        } finally {
            setDetectFileTypeOverlayActive(false);
        }
    };
    // Create-connection dialog helpers
    const onConnTypeChange = (e) => {
        const selectedType = e?.value?.id || "S3";
        setConnForm((p) => ({...p, type: selectedType}));
    };
    const closeConnDialog = () => setShowConnDialog(false);
    const handleNewGroupCreated = async (newGroupName) => {
        // console.log("handleNewGroupCreated1 newGroupName=", newGroupName);
        setNewGroupName(newGroupName);
        setOpenEditCreateDialog(false); // Close the dialog
        setSubmitOverlayActive(true);
        let newGroupArray;
        try {
            await semcastAxios.get(GROUPS_ACCESSIBLE_PIXELS, {withCredentials: true})
                .then((res) => {
                    newGroupArray = res.data.data;
                })
        } catch (error) {
            console.log(error)
            showErrorToastMessage("Error accessing Group information. " + error);
        }
        // const newGroupArray = await getAvailableGroups(); // Assume this refreshes the groups list and includes the new group

        if (newGroupArray.length > 0) {
            // console.log("handleNewGroupCreated newGroupArray fetched and used immediately:", newGroupArray);
            const groups = newGroupArray.map(item => ({
                group_name: item.group_name,
                id: item.resource_group_id
            }));
            // console.log("handleNewGroupCreated2 groups=", groups);
            // Assuming the newly created group is included in the newly fetched list
            const newGroup = groups.find(group => group.group_name === newGroupName);
            // console.log("handleNewGroupCreated3 newGroup=", newGroup);
            if (newGroupName) {
                // props.formik.setFieldValue('group_id', newGroup.id);

                setCreatedGroupId(newGroup.id);
                // console.log("handleNewGroupCreated4 BeforeNewOption newGroup=", newGroupName);
                const newGroupIndex = groups.findIndex(group => group.group_name === newGroupName);
                // console.log("handleNewGroupCreated5 BeforeNewOption newGroupIndex=", newGroupIndex);

                let newGroupsList;
                if (newGroupIndex !== -1) {
                    const [removed] = groups.splice(newGroupIndex, 1);
                    // console.log("handleNewGroupCreated6 Removed group:", removed);
                    newGroupsList = [removed, {group_name: "Create New Group", id: "new"}, ...groups];
                } else {
                    newGroupsList = [{group_name: "Create New Group", id: "new"}, ...groups];
                }
                // console.log("handleNewGroupCreated7 AfterNewGroupGroups=", groups);

                // console.log("handleNewGroupCreated8 newGroupsList=", newGroupsList);
                if (JSON.stringify(groupsWithNewOption) !== JSON.stringify(newGroupsList)) {
                    setGroupsWithNewOption(newGroupsList);
                    // console.log("handleNewGroupCreated8 NewGroupsWithNewOption=", groupsWithNewOption);
                    setSelectedGroup(newGroupsList.find(group => group.id === newGroup.id));

                    const idNum =
                        newGroup.id === "-1" || newGroup.id === -1 ? null : (typeof newGroup.id === "string" ? Number(newGroup.id) : newGroup.id);
                    setRecordForm((prev) => ({...prev, groupId: idNum}));
                }
            }
        } else {
            console.error("newGroupArrayNotFetched");
        }
        setSubmitOverlayActive(false);
    };

    // schedule payload like original
    function schedulePayload(form) {
        switch (form.scheduleType) {
            case "DAILY":
                return {schedule: {type: "DAILY"}};
            case "WEEKLY":
                return {
                    schedule: {
                        type: "WEEKLY",
                        dayOfWeek: (getById(dayOfWeekOptions, form.scheduleWeeklyDay)?.name || "").toUpperCase()
                    }
                };
            case "MONTHLY":
                return {schedule: {type: "MONTHLY", dayOfMonth: form.scheduleMonthlyDate ?? 1}};
            case "UNSCHEDULED":
                return {schedule: {type: "UNSCHEDULED"}};
            default:
                return {schedule: {type: "DAILY"}};
        }
    }

    const validateAndConfirm = () => {
        setDemographicErrorsVisible(true);
        if (Object.keys(validateDemographicForm(demographicForm, supportsDemographicSelection(pipelineType?.id, selectedFileType))).length) {
            showErrorToastMessage("Please complete demographic person selection before saving.", 8);
            return;
        }
        setFieldErrors({
            name: false,
            inputConn: false,
            outputConn: false,
            inputFile: false,
            weeklyDay: false,
            group: false,
            scheduleType: false,
            compressionType: false,
            fileType: false,
        });


        const trimmedName = (recordForm.name || "").trim();
        const fail = (fields, focusRef, message) => {
            setFieldErrors((prev) => ({...prev, ...fields}));
            focusRef?.current?.focus?.();
            // showErrorToastMessage(message, 8);
            return false;
        };

        //workflow name (required)
        if (!trimmedName) return fail({name: true}, nameRef, "Please provide a Workflow Name.");
        // Save to group (required)
        if (recordForm.groupId == null) {
            return fail({group: true}, groupRef, "Please choose a group.");
        }
        // Schedule type (required)
        if (!recordForm.scheduleType) {
            return fail({scheduleType: true}, scheduleTypeRef, "Please choose a schedule.");
        }
        // Schedule day or date (required)
        if (recordForm.scheduleType === "WEEKLY" && recordForm.scheduleWeeklyDay == null) return fail({weeklyDay: true}, weeklyDayRef, "Please choose a day of the week.");
        if (isFileExchange) {
            // Input connection (required when file exchange)
            if (!recordForm.inputConnId) return fail({inputConn: true}, inputConnRef, "Please select an Input Connection.");
            // Input file optional when editing
        }
        // Output connection (required)
        if (!recordForm.outputConnId) return fail({outputConn: true}, outputConnRef, "Please select an Output Connection.");
        if (isFileExchange) {
            // Detected input file type (required)
            if (!selectedFileType) {
                return fail({fileType: true}, fileTypeRef, "Detected input file type is required.");
            }
            // Client Id column optional even when columns are detected
            // Compression type (required)
            if (!compressionType) {
                return fail({compressionType: true}, compressionRef, "Input File Compression type is required.");
            }

        }


        const inputConn = byId(recordForm.inputConnId)?.name || "";
        const outputConn = byId(recordForm.outputConnId)?.name || "";
        const scheduleStr = scheduleLabel(recordForm);
        const scheduleHint = scheduleConfirmText(recordForm);
        const formatValue = (val) => {
            if (val === null || val === undefined) return "(none)";
            if (typeof val === "number") return val.toLocaleString();
            if (typeof val === "string") {
                const trimmed = val.trim();
                return trimmed.length ? trimmed : "(none)";
            }
            return val;
        };
        const buildAttributeDetails = (node, path = []) => {
            const entries = [];
            if (node == null) {
                return entries;
            }
            if (Array.isArray(node)) {
                const clean = node
                    .map((val) => (val == null ? "" : String(val).trim()))
                    .filter(Boolean);
                if (clean.length) {
                    const label = path.join(" > ") || "Attributes";
                    entries.push(`${label}: ${clean.join(", ")}`);
                }
                return entries;
            }
            if (typeof node === "object") {
                Object.entries(node).forEach(([key, value]) => {
                    entries.push(...buildAttributeDetails(value, [...path, key]));
                });
                return entries;
            }
            const text = String(node).trim();
            if (text) {
                const label = path.join(" > ") || "Attributes";
                entries.push(`${label}: ${text}`);
            }
            return entries;
        };
        const attributeDetails = buildAttributeDetails(selectedAttributes);

        const sections = [
            {
                title: "Workflow",
                rows: [
                    {label: "Workflow Name", value: formatValue(trimmedName)},
                    {label: "Group", value: formatValue(selectedGroup?.group_name)},
                    {label: "Schedule", value: formatValue(scheduleStr), hint: scheduleHint},
                    {label: "Description", value: formatValue(recordForm.description)},
                ],
            },
        ];
        sections.push({
            title: "Additional options",
            rows: [
                {
                    label: "Transformation Processing",
                    value: transformationProcessingCheckbox ? "Enabled" : "Disabled",
                },
                {
                    label: "Processing Mode",
                    value: transformationProcessingCheckbox ? formatValue(transformationProcessingMode) : "(none)",
                },
            ],
        });

        if (isFileExchange) {
            sections.push({
                title: "Input",
                rows: [
                    {label: "Input Connection", value: formatValue(inputConn)},
                    {label: "Selected File", value: formatValue(recordForm.inputFileKey)},
                    {label: "Detected File Type", value: formatValue(selectedFileType)},
                    {label: "Client ID Column", value: formatValue(selectedClientIdColumn)},
                    {label: "Input Compression Type", value: formatValue(compressionType)},
                    {
                        label: "Additional Returned Columns",
                        value: formatValue(selectedAdditionalClientColumns.join(", "))
                    },
                ],
            });
        }

        sections.push({
            title: "Output",
            rows: [{label: "Output Connection", value: formatValue(outputConn)}],
        });

        sections.push({
            title: "Attributes",
            rows: [{
                label: "Selected Attributes",
                value: `${formatValue(selectedAttributesCount || 0)} selected`,
                details: attributeDetails
            }],
        });

        setConfirmDetails(sections);
        setConfirmOpen(true);
        return true;
    };
    useEffect(() => {
        console.log("selectedAttributes", selectedAttributes)
    }, [selectedAttributes]);


    const handleUpdateRecord = async () => {
        setTriedSubmit(true); // keep same flag behavior
        closeConfirmDialog();
        const usingFile = isFileExchange;
// guard values

        try {
            setSubmitOverlayActive(true);

            const selectedId =
                (typeof recordForm.groupId === "object" ? recordForm.groupId?.id : recordForm.groupId) ??
                selectedGroup?.id ??
                window.__currentGroupId ??
                null;

            const group_id = selectedId != null ? JSON.stringify(selectedId) : null; // keep behavior
            const pipeline_name = (recordForm.name || "").trim();
            const descriptionValue = (recordForm.description || "").trim();
            const description = descriptionValue.length ? descriptionValue : null;

            const file_type =
                selectedFileType ||
                (Array.isArray(detectMapInfo?.fileTypes) && detectMapInfo.fileTypes[0]?.type) ||
                "unknown";

            const incoming_delimiter = usingFile ? (detectMapInfo?.delimiter ?? null) : null;
            const incoming_layout = usingFile ? (detectMapInfo ? JSON.stringify(detectMapInfo) : null) : null;
            const compression_type = usingFile ? (compressionType || null) : null;

            const incoming_ftp_settings_id = usingFile ? (byId(recordForm.inputConnId)?._raw?.id ?? null) : null;
            const outgoing_ftp_settings_id = byId(recordForm.outputConnId)?._raw?.id ?? null;

            const match_return_properties =
                typeof finalSelectedAttributes === "string"
                    ? finalSelectedAttributes
                    : JSON.stringify(finalSelectedAttributes || {});
            const schedule_properties = JSON.stringify({...schedulePayload(recordForm), prepend_one_to_phone: phoneSelected && prependOneToPhone});
            const pipeline_type = pipelineType.id;
            const status = recordForm.status;
            const usingFileExchange = (pipelineType?.id || "FILE_EXCHANGE") === "FILE_EXCHANGE";
            const usingFolderExchange = (pipelineType?.id || "FOLDER_EXCHANGE") === "FOLDER_EXCHANGE";
            const payload = usingFileExchange || usingFolderExchange
                ? {
                    // FILE_EXCHANGE / FOLDER_EXCHANGE
                    group_id,
                    pipeline_name,
                    incoming_delimiter,
                    file_type,
                    incoming_layout,
                    compression_type,
                    incoming_ftp_settings_id,
                    outgoing_ftp_settings_id,
                    match_return_properties,
                    schedule_properties,
                    pipeline_type,
                    client_id_column: selectedClientIdColumn,
                    description,
                    incoming_return_columns: selectedAdditionalClientColumns.length ? selectedAdditionalClientColumns : null,
                    external_transformation: transformationProcessingCheckbox,
                    on_delivery_instructions: transformationProcessingCheckbox ? transformationProcessingMode : null,
                }
                : {
                    // NON-FILE_EXCHANGE (HOUSEHOLD_EXPORT / PERSON_EXPORT / BUSINESS_EXPORT)
                    group_id,
                    pipeline_name,
                    // incoming_delimiter,
                    // file_type,
                    // incoming_layout,
                    // compression_type,
                    // incoming_ftp_settings_id,
                    outgoing_ftp_settings_id,
                    match_return_properties,
                    schedule_properties,
                    pipeline_type,
                    description,
                    external_transformation: transformationProcessingCheckbox,
                    on_delivery_instructions: transformationProcessingCheckbox ? transformationProcessingMode : null,
                    // incoming_return_columns: selectedAdditionalClientColumns.length ? selectedAdditionalClientColumns : null,
                    // client_id_column: selectedClientIdColumn,
                };
            Object.assign(payload, demographicProfilePayload(demographicForm, pipelineType?.id, selectedFileType, true));
            console.log(payload)

            await semcastAxios.put(IDENTITY_PIPELINE + "/" + pipelineId, payload, {withCredentials: true});
            showSuccessToastMessage("Pipeline updated.", 6);

            onCreated?.();
            onClose?.();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Failed to create pipeline.";
            showErrorToastMessage(msg, 10);
        } finally {
            setSubmitOverlayActive(false);
        }
    };


    const handleClose = () => {
        setFieldErrors(INITIAL_FIELD_ERRORS);
        setTriedSubmit(false);
        onClose?.();
    };
// near the imports of dayOfWeekOptions/getById
    const dayNameToId = (name) => {
        if (!name) return null;
        const hit = dayOfWeekOptions.find(d => d.name.toUpperCase() === String(name).toUpperCase());
        return hit ? hit.id : null;
    };
    /**************************** Render ***************************/
    return (
        <>
            {/* ---- Create Record Dialog (UI preserved) ---- */}
            <Dialog onClose={handleClose} title={user?.isAdmin ? `Edit Identity Pipeline (Super Admin)` : `Edit Identity Pipeline`} className="ip-dialog create-identity-dialog">
                <LoadOverlay active={submitOverlayActive}>
                    <div className="ip-form">
                        <div className="ip-section">
                            <div className="ip-section-title required-asterisk">Workflow Name</div>
                            <div className="ip-field">
                                <input
                                    ref={nameRef}
                                    className={fieldErrors.name && !(recordForm.name || "").trim() ? "ip-field ip-error" : "ip-field"}
                                    name="name"
                                    value={recordForm.name}
                                    onChange={onRecordTextChange}
                                    placeholder="e.g., CRM Ingest - Q4"
                                />
                            </div>
                        </div>

                        <div className="ip-two-col">
                            {user?.isAdmin && <div className="ip-section">
                                <div className="ip-section-title required-asterisk">Export Type</div>
                                <div className="ip-field">
                                    <DropDownList
                                        data={EXPORT_TYPE_OPTIONS}
                                        textField="name"
                                        dataItemKey="id"
                                        value={pipelineType}
                                        onChange={(e) => {
                                            const next = e.value;
                                            setPipelineType(next);
                                            setFolderExchangeInfoOpen(next?.id === "FOLDER_EXCHANGE");

                                            // when switching away from File Exchange, clear file-related bits + coerce schedule if DAILY
                                            setFieldErrors({});
                                            setTriedSubmit(false);
                                            if (next.id !== "FILE_EXCHANGE") {
                                                setRecordForm(prev => ({
                                                    ...prev,
                                                    inputConnId: null,
                                                    inputFileKey: null,
                                                    // if DAILY selected, move to WEEKLY by default
                                                    scheduleType: prev.scheduleType === "DAILY" ? "WEEKLY" : prev.scheduleType,
                                                    scheduleWeeklyDay: null,
                                                    scheduleMonthlyDate: null,
                                                }));
                                                setSelectedFileType(null);
                                                setCompressionType(null);
                                                // also clear any detect-map ui state you keep (if you hold one)
                                                // setDetectMapInfo?.(null);
                                            }
                                        }}
                                    />
                                </div>
                            </div>}
                            <div className="ip-section">
                                <div className={fieldErrors.group && !recordForm.groupId ? "ip-error" : ""}>
                                    <LoadOverlay active={createGroupOverlayActive} width={"100%"} height={"90px"}>
                                        <div className="ip-section-title required-asterisk">Save To Group</div>
                                        <DropDownList
                                            ref={setDomRef(groupRef)}
                                            data={groupsWithNewOption}
                                            textField="group_name"
                                            dataItemKey="id"
                                            defaultItem={DEFAULT_GROUP_ITEM}
                                            onChange={onChangeGroupDropDown}
                                            value={selectedGroup}
                                            className="fields dropdown-standard"
                                        />
                                    </LoadOverlay>
                                </div>
                                {fieldErrors.group && !recordForm.groupId &&
                                    <div className="ip-test-msg error">Please choose a group.</div>}
                                {/*</div>*/}
                                <div className={"k-form-hint"}>
                                    You must be an Admin of a group with permission to create an Identity Pipeline.
                                </div>

                            </div>
                        </div>

                        <div className="ip-section">
                            <div className="ip-section-title">Description (optional)</div>
                            <div className="ip-field">
              <textarea
                  name="description"
                  value={recordForm.description || ""}
                  onChange={onRecordTextChange}
                  placeholder="Description of this workflow"
                  rows={3}
                  style={{resize: "vertical"}}
              />
                            </div>
                        </div>

                        <div className="ip-two-col">
                            {isFileExchange && <div className="ip-field">
                                <div className={fieldErrors.inputConn ? "ip-section ip-error" : "ip-section"}>
                                    <div className="ip-section-title required-asterisk">Input Connections</div>
                                    <div className="ip-inline">
                                        <DropDownList
                                            ref={inputConnRef}
                                            // className={fieldErrors.inputConn ? "ip-error" : ""}
                                            data={connectionOptions}
                                            textField="name"
                                            dataItemKey="id"
                                            value={ddValueConn(recordForm.inputConnId)}
                                            onChange={onSelectConn("input")}
                                            disabled={loadingConns}
                                            defaultItem={{
                                                id: null,
                                                name: loadingConns ? "Loading…" : "Select input connection…"
                                            }}
                                        />
                                        <Button
                                            // ref={chooseFileBtnRef}
                                            ref={(el) => {
                                                chooseFileBtnRef.current = el?.element ?? el;
                                            }}
                                            className={fieldErrors.inputFile ? "ip-error" : ""}
                                            style={{marginLeft: 8}}
                                            disabled={!recordForm.inputConnId}
                                            onClick={openInputBrowser}
                                        >
                                            Choose a file
                                        </Button>

                                    </div>
                                    {fieldErrors.inputFile && (
                                        <div className={"ip-test-msg error"}>
                                            Please choose an input file.
                                        </div>
                                    )}
                                    <div className="k-form-hint">
                                        The chosen file must be the only file in its folder.
                                    </div>
                                    {recordForm.inputFileKey && (
                                        <div style={{fontSize: 14, color: "#555", marginTop: 4}}>
                                            Selected file: {recordForm.inputFileKey}
                                        </div>
                                    )}
                                </div>
                            </div>}

                            <div className="ip-field">
                                <div className={fieldErrors.outputConn ? "ip-section ip-error" : "ip-section"}>
                                    <div className="ip-section-title required-asterisk">Output Connections</div>
                                    <DropDownList
                                        ref={outputConnRef}
                                        // className={fieldErrors.outputConn && !recordForm.outputConnId ? "ip-field ip-error" : "ip-field"}
                                        data={connectionOptions}
                                        textField="name"
                                        dataItemKey="id"
                                        value={ddValueConn(recordForm.outputConnId)}
                                        onChange={onSelectConn("output")}
                                        disabled={loadingConns}
                                        defaultItem={{
                                            id: null,
                                            name: loadingConns ? "Loading…" : "Select output connection…"
                                        }}
                                    />
                                </div>
                            </div>

                            {/*<div className="ip-field">*/}
                            {isFileExchange && <div
                                className={fieldErrors.fileType && !selectedFileType ? "ip-section ip-error" : "ip-section"}>
                                <LoadOverlay active={detectFileTypeOverlayActive} width={"100%"} height={"100px"}>
                                    <div className="ip-section-title required-asterisk">Detected Input File Type</div>
                                    <div ref={setDomRef(fileTypeRef)}>
                                        <FileTypeSelector
                                            options={detectedFileTypes}
                                            value={selectedFileType}
                                            onChange={(type) => {
                                                setSelectedFileType(type);
                                                setFieldErrors((prev) => ({...prev, fileType: false}));
                                                setTriedSubmit(false);
                                            }
                                            }
                                            disabled={!detectedFileTypes?.length}
                                        />
                                    </div>
                                    {fieldErrors.fileType && !selectedFileType && (
                                        <div className="ip-test-msg error">Detected input file type is required.</div>
                                    )}
                                    <div className="k-form-hint">Use the detected suggestion or pick a different one.
                                    </div>
                                </LoadOverlay>
                            </div>}
                            {/*</div>*/}

                            {/* New: Client Id Column selection */}
                            {isFileExchange && (
                                <div className="ip-section">
                                    <LoadOverlay active={detectFileTypeOverlayActive} width={"100%"} height={"100px"}>
                                        <div className="ip-section-title">Client Id (optional)</div>
                                        <div ref={setDomRef(clientidColumnRef)} tabIndex={-1}>
                                            <DropDownList
                                                data={detectedColumns.map(s => ({text: s, value: s}))}
                                                textField="text"
                                                dataItemKey="value"
                                                value={selectedClientIdColumn ? {
                                                    text: selectedClientIdColumn,
                                                    value: selectedClientIdColumn
                                                } : null}
                                                onChange={(e) => {
                                                    const val = e.value?.value ?? null;
                                                    setSelectedClientIdColumn(val);
                                                    setFieldErrors(prev => ({...prev, clientIdColumn: false}));
                                                    setTriedSubmit(false);
                                                }}
                                                disabled={!detectedColumns.length}
                                                defaultItem={{
                                                    text: detectedColumns.length ? "Select client id column…" : "No columns detected",
                                                    value: null
                                                }}
                                            />
                                        </div>
                                        {/*{fieldErrors.clientIdColumn && (!selectedClientIdColumn) && (*/}
                                        {/*    <div className="k-text-error" style={{marginTop: 4}}>Required</div>*/}
                                        {/*)}*/}
                                        <div className="k-form-hint">Choose which column in your file represents the
                                            client ids.
                                        </div>
                                    </LoadOverlay>
                                </div>
                            )}

                            {/*<div className="ip-field">*/}
                            {isFileExchange && <div
                                className={fieldErrors.compressionType && (!compressionType) ? "ip-section ip-error" : "ip-section"}>
                                <div className="ip-section-title required-asterisk">Input Compression Type</div>
                                <div ref={setDomRef(compressionRef)} tabIndex={-1}>
                                    <DropDownList
                                        id="compression-type-ddl"
                                        aria-describedby="compression-help"
                                        data={COMPRESSION_OPTIONS}
                                        textField="text"
                                        dataItemKey="value"
                                        defaultItem={COMPRESSION_DEFAULT_ITEM}
                                        value={COMPRESSION_OPTIONS.find((o) => o.value === compressionType) || null}
                                        onChange={(e) => {
                                            setCompressionType(e.value?.value ?? null);
                                            setFieldErrors((prev) => ({...prev, compressionType: false}));
                                            setTriedSubmit(false);
                                        }}
                                    />
                                </div>
                                <div className="k-form-hint" id="compression-help" style={{margin: "4px 0 6px"}}>
                                    Select the compression type of the input files.
                                </div>
                                {triedSubmit && !compressionType && (
                                    <div className="k-text-error" style={{marginTop: 4}}>Required</div>
                                )}
                            </div>}
                            {isFileExchange && (
                                <div className="ip-section">
                                    <LoadOverlay active={detectFileTypeOverlayActive} width={"100%"} height={"100px"}>
                                        <div className="ip-section-title">Additional Returned Columns (optional)</div>
                                        <MultiSelect
                                            data={detectedColumnOptions}
                                            textField="text"
                                            dataItemKey="value"
                                            placeholder={
                                                detectedColumnOptions.length
                                                    ? "Select one or more columns…"
                                                    : "No columns detected"
                                            }
                                            // disabled={!detectedColumnOptions.length}
                                            value={selectedAdditionalClientColumns.map((col) => ({
                                                text: col,
                                                value: col
                                            }))}
                                            onChange={(e) =>
                                                setSelectedAdditionalClientColumns(
                                                    (e.value || []).map((item) => item.value)
                                                )
                                            }
                                        />
                                        <div className="k-form-hint">
                                            Choose any additional columns that you would like to receive. Please avoid selecting the column that matches the detected file type.
                                        </div>
                                    </LoadOverlay>
                                </div>
                            )}
                            <div className="ip-field">
                                <div
                                    className={fieldErrors.scheduleType && !recordForm.scheduleType ? "ip-section ip-error" : "ip-section"}>
                                    <div className="ip-section-title required-asterisk">Schedule</div>
                                    <div className="ip-schedule-row">
                                        <DropDownList
                                            ref={setDomRef(scheduleTypeRef)}
                                            data={SCHEDULE_TYPE_ALL}
                                            textField="name"
                                            dataItemKey="id"
                                            value={SCHEDULE_TYPE_ALL.find(x => x.id === recordForm.scheduleType) || null}
                                            onChange={onScheduleTypeChange}
                                        />
                                        {fieldErrors.scheduleType && !recordForm.scheduleType &&
                                            <div className="ip-test-msg error">Please choose a schedule.</div>}
                                        {recordForm.scheduleType === "WEEKLY" && (
                                            <DropDownList
                                                ref={weeklyDayRef}
                                                className={fieldErrors.weeklyDay ? "ip-error" : ""}
                                                data={dayOfWeekOptions}
                                                textField="name"
                                                dataItemKey="id"
                                                value={getById(dayOfWeekOptions, recordForm.scheduleWeeklyDay)}
                                                onChange={onWeeklyDayChange}
                                                defaultItem={{id: null, name: "Select a day…"}}
                                            />
                                        )}

                                        {/*{recordForm.scheduleType === "MONTHLY" && (*/}
                                        {/*    <div className="ip-schedule-fixed-note" style={{padding: "0 12px"}}>*/}
                                        {/*        Runs on the 1st of each month.*/}
                                        {/*    </div>*/}
                                        {/*)}*/}
                                    </div>
                                    <div className="k-form-hint">{scheduleConfirmText(recordForm)}</div>
                                </div>
                            </div>
                            <div className="ip-field">
                                <div className="ip-section">
                                    <div className="ip-section-title">Additional options</div>
                                    <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem"}}>
                                        <div className={"additional-option"} style={{display: "flex", alignItems: "center", gap: "0.35rem"}}>
                                            <Checkbox
                                                value={transformationProcessingCheckbox}
                                                onChange={handleTransformationProcessingChange}
                                                label={"Transformation Processing"}
                                            />
                                            <Tooltip
                                                position="right"
                                                anchorElement="target"
                                                tooltipStyle={{
                                                    width: "15rem",
                                                    borderRadius: "0.25rem",
                                                }}
                                            >
                                                <i className="fa fa-question-circle-o tooltip-question-icon"
                                                   aria-hidden="true"
                                                   title={"Select to persist results for more processing"}></i>
                                            </Tooltip>
                                        </div>
                                        {supportsPhoneFormatting && phoneSelected && (
                                            <div className="additional-option" style={{display: "flex", alignItems: "center", gap: "0.35rem"}}>
                                                <Checkbox
                                                    value={prependOneToPhone}
                                                    onChange={() => setPrependOneToPhone((previous) => !previous)}
                                                    label="Output phone numbers with country code"
                                                />
                                            </div>
                                        )}
                                        {transformationProcessingCheckbox && (
                                            <div style={{display: "flex", alignItems: "center", gap: "1rem", marginLeft: "1.7rem"}}>
                                                <RadioButton
                                                    name="transformation-processing-mode-edit-pipeline"
                                                    value="APPEND"
                                                    checked={transformationProcessingMode === "APPEND"}
                                                    onChange={handleTransformationModeChange}
                                                    label="Append"
                                                />
                                                <RadioButton
                                                    name="transformation-processing-mode-edit-pipeline"
                                                    value="REPLACE"
                                                    checked={transformationProcessingMode === "REPLACE"}
                                                    onChange={handleTransformationModeChange}
                                                    label="Replace"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/*</div>*/}
                        </div>
                        <div className="ip-section">
                            <DemographicSelectionForm value={demographicForm} onChange={setDemographicForm}
                                pipelineType={pipelineType?.id} fileType={selectedFileType}
                                showErrors={demographicErrorsVisible} idPrefix="edit-demographic"/>
                            <EditAppendTree
                                key={pipelineId}
                                availableAttributes={availableAttributes}
                                selectedAttributes={selectedAttributes}
                                setSelectedAttributesCount={setSelectedAttributesCount}
                                setFinalSelectedAttributes={setFinalSelectedAttributes}
                                setFinalAvailableAttributes={setFinalAvailableAttributes}
                                onChange={(obj) => setFinalSelectedAttributes(obj)}
                                exportType={pipelineType?.id || pipelineType?.value || "FILE_EXCHANGE"}
                            />
                        </div>

                    </div>

                    <DialogActionsBar>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button themeColor="primary" onClick={validateAndConfirm}>Update</Button>
                    </DialogActionsBar>
                </LoadOverlay>
            </Dialog>

            {/* Confirm */}
            {confirmOpen && (
                <Dialog title="Confirm Identity Pipeline" onClose={closeConfirmDialog} className="ip-dialog" width={"30vw"}>
                    <div className="ip-confirm-body">
                        {confirmDetails.length === 0 && (
                            <div className="ip-confirm-empty">No confirmation details to display.</div>
                        )}
                        {confirmDetails.map((section, sIdx) => (
                            <div className="ip-confirm-section" key={`${section.title}-${sIdx}`}>
                                <div className="ip-confirm-section-title">{section.title}</div>
                                <dl className="ip-confirm-list">
                                    {section.rows.map((row, rIdx) => (
                                        <React.Fragment key={`${section.title}-${row.label}-${rIdx}`}>
                                            <dt>{row.label}</dt>
                                            <dd>
                                                <div className="ip-confirm-value">{row.value}</div>
                                                {row.hint && <div className="ip-confirm-hint">{row.hint}</div>}
                                                {Array.isArray(row.details) && row.details.length > 0 && (
                                                    <ul className="ip-confirm-sublist">
                                                        {row.details.map((detail, idx) => (
                                                            <li key={`${section.title}-${row.label}-detail-${idx}`}>{detail}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </dd>
                                        </React.Fragment>
                                    ))}
                                </dl>
                            </div>
                        ))}
                    </div>
                    <DialogActionsBar>
                        <Button onClick={closeConfirmDialog}>Cancel</Button>
                        <Button themeColor="primary" onClick={handleUpdateRecord}>Update</Button>

                    </DialogActionsBar>
                </Dialog>
            )}
            {folderExchangeInfoOpen && (
                <Dialog
                    title="Folder Exchange Requirements"
                    onClose={() => setFolderExchangeInfoOpen(false)}
                    className="ip-dialog"
                    width={"30vw"}
                >
                    <div className="ip-section" style={{marginBottom: 0}}>
                        <p style={{marginBottom: 12, color: "rgb(66, 66, 66)"}}>
                            To use <strong>Folder Exchange</strong>, please make sure:
                        </p>
                        <ul style={{paddingLeft: 18, marginBottom: 0}}>
                            <li>The connection you pick is an S3 bucket we can access.</li>
                            <li>The file path you provide follows <code>connection/YYYY/MM/DD/HH</code>.</li>
                        </ul>
                    </div>
                    <DialogActionsBar>
                        <Button onClick={() => setFolderExchangeInfoOpen(false)} className="button-standard">
                            Got it
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/* File Browser */}
            {showInputBrowser && (
                <FileBrowser
                    isOpenModel={isOpenInputBrowserModal}
                    visible={true}
                    setFileBrowserVisible={setShowInputBrowser}
                    handleClose={handleInputBrowserClose}
                    settings={byId(recordForm.inputConnId)?._raw?.id}
                    connectionTitle={byId(recordForm.inputConnId)?.name || "Select Input File"}
                />
            )}
            {/*Create New Group Dialog*/}
            {openEditCreateDialog && (
                <PermissionsEditGroup
                    dialogMode={"new"}
                    editGroupObj={{}}
                    refreshTable={async () => false}
                    allUsersRoles={allUsersRoles}
                    // usersLoad = {usersLoad}
                    userHelpers={userHelpers}
                    openEditCreateDialog={createGroupDialogOpen}
                    setOpenEditCreateDialog={setOpenEditCreateDialog}
                    groupEditFormik={groupEditFormik}
                    isFromIdxTag={true}
                    onGroupCreated={handleNewGroupCreated} // Add this callback
                />
            )}
            {/* Create Connection Dialog (identical UI) */}
            {showConnDialog && (
                <Dialog onClose={closeConnDialog} title={"Create Connection"} className="ip-dialog" width={"40vw"}>
                    <div className="ip-form connection-form">
                        <div className="ip-two-col">
                            <div className="ip-field w-25">
                                <span>Type</span>
                                <DropDownList
                                    data={CONNECTION_TYPE_OPTIONS}
                                    textField="name"
                                    dataItemKey="id"
                                    value={CONNECTION_TYPE_OPTIONS.find((opt) => opt.id === connForm.type) || CONNECTION_TYPE_OPTIONS[0]}
                                    onChange={onConnTypeChange}
                                    className="dropdown-standard"
                                />
                            </div>
                        </div>

                        {connForm.type === "S3" && (
                            <S3EmbeddedForm
                                onBusyChange={() => {
                                }}
                                onCancel={closeConnDialog}
                                onSaved={async (name) => {
                                    const latest = await reloadConnections();
                                    const match = latest.find((c) => c.type === "S3" && c.name === name);
                                    if (match) {
                                        if (connDialogOrigin === "input")
                                            setRecordForm((p) => ({...p, inputConnId: match.id}));
                                        else
                                            setRecordForm((p) => ({...p, outputConnId: match.id}));
                                    }
                                    setShowConnDialog(false);
                                }}
                            />
                        )}

                        {connForm.type === "FTP" && (
                            <FTPEmbeddedForm
                                onBusyChange={() => {
                                }}
                                onCancel={closeConnDialog}
                                onSaved={async (name) => {
                                    const latest = await reloadConnections();
                                    const match = latest.find((c) => c.name === name && (c.type === "SFTP" || c.type === "FTPS"));
                                    if (match) {
                                        if (connDialogOrigin === "input")
                                            setRecordForm((p) => ({...p, inputConnId: match.id}));
                                        else
                                            setRecordForm((p) => ({...p, outputConnId: match.id}));
                                    }
                                    setShowConnDialog(false);
                                }}
                            />
                        )}
                    </div>
                </Dialog>
            )}
            {/*no file type detected window*/}
            {noTypeDialogOpen && (
                <Dialog
                    title="We couldn’t recognize this file"
                    onClose={() => setNoTypeDialogOpen(false)}
                    className="ip-dialog"
                    width={650}
                >
                    <div className="ip-section" style={{marginBottom: 0}}>
                        {renderDetectMapInsights(noTypeInfo)}
                        {!noTypeInfo?.thoughts && (
                            <div style={{marginTop: 8, fontSize: 12, color: "#666"}}>
                                Tip: verify the file has a header row and a consistent delimiter (comma, tab, or pipe),
                                and
                                that it matches one of our supported types (Raw Email, Address, Devices, IP+Timestamp).
                            </div>
                        )}
                    </div>

                    <DialogActionsBar>
                        <Button onClick={() => setNoTypeDialogOpen(false)} className="button-standard button-white">
                            Close
                        </Button>
                        <Button
                            onClick={() => {
                                setNoTypeDialogOpen(false);
                                setSelectedFileType(null);
                                setDetectedFileTypes([]);
                                setRecordForm(prev => ({...prev, inputFileKey: null}));
                                setInputFilePath("");
                                // optionally:
                                openInputBrowser();
                            }}
                            className="button-standard"
                        >
                            Choose a different file
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {connError && (
                <div style={{color: "#c62828", marginBottom: 8}}>
                    Failed to load connections: {connError}
                </div>
            )}


        </>
    );
}
